import { SortOrder } from 'antd/lib/table/interface';
import { PaymentMethod, ProjectPlanPriority, ProjectPlanStatus } from 'api/models';
import {
  CollectionChannel,
  CollectionActionType,
  CollectionLogStatus,
} from 'pages/debt-collection/debtCollectionApi';

export const SortTypeLong = {
  ASCEND: 'ascend',
  DESCEND: 'descend',
};

export const SortTypeShort = {
  ASC: 'ASC',
  DESC: 'DESC',
};

export const SortTypeMaper = {
  [SortTypeLong.ASCEND]: SortTypeShort.ASC,
  [SortTypeLong.DESCEND]: SortTypeShort.DESC,
};
export const sortDirections = [
  SortTypeLong.DESCEND as SortOrder,
  SortTypeLong.ASCEND as SortOrder,
  SortTypeLong.DESCEND as SortOrder,
];

export const EMAIL_REGEX_PATTERN = "/^[a-zA-Z0-9.! #$%&'*+/=? ^_`{|}~-]+@[a-zA-Z0-9-]+(?:. [a-zA-Z0-9-]+)*$/";

export const PredefinedRoles = {
  SUPER_ADMINISTRATOR: 'Administrator',
  ORGANIZATION: 'Organization',
};

export interface FilterKey {
  id: number | undefined;
  key: string;
  name: string;
}

export const filterKeys: FilterKey[] = [
  { id: -1, key: 'global:select.all', name: 'All' },
  { id: 1, key: 'global:select.active', name: 'Active' },
  { id: 0, key: 'global:select.inactive', name: 'Inactive' },
];

export const ProjectPlanStatusConfig: Partial<Record<
  ProjectPlanStatus,
  { key: string; title: string; color: string }
>> = {
  [ProjectPlanStatus.Pending]: { key: 'pending', title: 'Pending', color: 'orange' },
  [ProjectPlanStatus.InProgress]: { key: 'in-progress', title: 'In Progress', color: 'blue' },
  [ProjectPlanStatus.Completed]: { key: 'completed', title: 'Completed', color: 'green' },
  [ProjectPlanStatus.Cancelled]: { key: 'cancelled', title: 'Cancelled', color: 'red' },
};

export const getProjectPlanStatusColor = (status: ProjectPlanStatus) => {
  switch (status) {
    case ProjectPlanStatus.Pending: // Pending
      return '#13c2c2';
    case ProjectPlanStatus.InProgress: // In Progress
      return '#1890ff';
    case ProjectPlanStatus.Completed: // Completed
      return '#52c41a';
    case ProjectPlanStatus.Cancelled: // Cancelled
      return '#ff4d4f';
    default:
      return '#d9d9d9';
  }
};

export const getProjectPlanStatusName = (status: ProjectPlanStatus) => {
  return ProjectPlanStatusConfig[status];
};

export const getPriorityColor = (priority: number | null): string => {
  switch (priority) {
    case ProjectPlanPriority.High:
      return '#ff4d4f';
    case ProjectPlanPriority.Medium:
      return '#faad14';
    case ProjectPlanPriority.Low:
      return '#52c41a';
    default:
      return '#d9d9d9';
  }
};

export const getPriorityName = (priority: number | null): { key: string; title: string } => {
  switch (priority) {
    case ProjectPlanPriority.High:
      return { key: 'high', title: 'High' };
    case ProjectPlanPriority.Medium:
      return { key: 'medium', title: 'Medium' };
    case ProjectPlanPriority.Low:
      return { key: 'low', title: 'Low' };
    default:
      return { key: 'none', title: 'None' };
  }
};

export const PaymentMethodConfig: Partial<Record<PaymentMethod, { key: string; title: string; color: string }>> = {
  [PaymentMethod.Cash]: { key: 'cash', title: 'Cash', color: 'green' },
  [PaymentMethod.Bank]: { key: 'bank-transfer', title: 'Bank Transfer', color: 'blue' },
  [PaymentMethod.Crypto]: { key: 'crypto', title: 'Crypto', color: 'purple' },
  [PaymentMethod.Other]: { key: 'other', title: 'Other', color: 'orange' },
};

export const getPaymentMethodColor = (method: PaymentMethod | null): string => {
  switch (method) {
    case PaymentMethod.Cash:
      return '#52c41a';
    case PaymentMethod.Bank:
      return '#1890ff';
    case PaymentMethod.Crypto:
      return '#faad14';
    case PaymentMethod.Other:
      return '#722ed1';
    default:
      return '#d9d9d9';
  }
};

export const getPaymentMethodName = (method: PaymentMethod | null): { key: string; title: string } => {
  const config = method !== null ? PaymentMethodConfig[method] : null;
  return config || { key: 'none', title: 'None' };
};

// ─── Debt Collection ─────────────────────────────────

export const CollectionChannelConfig: Record<
  CollectionChannel,
  { key: string; title: string; color: string; icon: string }
> = {
  [CollectionChannel.Email]: { key: 'email', title: 'Email', color: 'blue', icon: 'mail' },
  [CollectionChannel.Sms]: { key: 'sms', title: 'SMS', color: 'green', icon: 'smartphone' },
  [CollectionChannel.WhatsApp]: { key: 'whatsapp', title: 'WhatsApp', color: '#25D366', icon: 'message-circle' },
  [CollectionChannel.Telegram]: { key: 'telegram', title: 'Telegram', color: '#0088cc', icon: 'send' },
  [CollectionChannel.Push]: { key: 'push', title: 'Push', color: 'orange', icon: 'bell' },
  [CollectionChannel.InternalNotification]: { key: 'internal', title: 'Internal', color: 'purple', icon: 'inbox' },
  [CollectionChannel.Webhook]: { key: 'webhook', title: 'Webhook', color: 'cyan', icon: 'link' },
};

export const CollectionLogStatusConfig: Record<
  CollectionLogStatus,
  { key: string; title: string; color: string }
> = {
  [CollectionLogStatus.Pending]: { key: 'pending', title: 'Pending', color: 'orange' },
  [CollectionLogStatus.Sent]: { key: 'sent', title: 'Sent', color: 'blue' },
  [CollectionLogStatus.Delivered]: { key: 'delivered', title: 'Delivered', color: 'green' },
  [CollectionLogStatus.Failed]: { key: 'failed', title: 'Failed', color: 'red' },
  [CollectionLogStatus.Skipped]: { key: 'skipped', title: 'Skipped', color: 'default' },
};

export const CollectionActionTypeConfig: Record<
  CollectionActionType,
  { key: string; title: string; color: string }
> = {
  [CollectionActionType.SendMessage]: { key: 'send-message', title: 'Send Message', color: 'blue' },
  [CollectionActionType.EscalateToManager]: { key: 'escalate', title: 'Escalate', color: 'orange' },
  [CollectionActionType.FlagDeal]: { key: 'flag-deal', title: 'Flag Deal', color: 'red' },
  [CollectionActionType.Webhook]: { key: 'webhook', title: 'Webhook', color: 'cyan' },
};
